import { useCallback, useEffect, useState } from 'react';

import { Box, Flex, Skeleton } from '@chakra-ui/react';
import { Card, CardContent } from '@mesh.ai/rabbit';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import { L3_PATHS } from 'Deer/constants/routes';
import { DeerBreadcrumbs } from 'Deer/intent/components/Layout/Layout';
import {
  useGetSurveyForm,
  useGetSurveySelectedTemplate,
  useGetSurveySettings,
} from 'Deer/pages/Company/Engagement/Surveys/SurveyCreation/dataHook';
import { EMPLOYEE_PRIVACY_VALUES } from 'Deer/pages/Company/Engagement/Surveys/SurveyCreation/types';
import { useSelectIsL2Visible } from 'Deer/slice/DeerNav.slice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import useReducerWithThunk from 'components/hooks/useReducerWithThunk';
import { parseISO } from 'date-fns';
import ColorCodedDate from 'models/common/ColorCodedDate';
import { useParams } from 'react-router-dom';
import {
  addPreviewSurveyDetailsAction,
  addSurveyDetailsAction,
  moveToNextQuestionAction,
  moveToPreviousQuestionAction,
} from 'screens/Engagement/pulseSurveyActions';
import pulseSurveyReducer, { pulseSurveyInitialState } from 'screens/Engagement/pulseSurveyReducer';
import { debounce } from 'throttle-debounce';

import { isNetchexLogin } from 'constants/netChexConstants';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EngagementQuestionCard } from './components/survey-form/EngagementQuestionCard';
import { ScrollableQuestionnaire } from './components/survey-form/ScrollableQuestionnaire';
import { SurveyFormFooter } from './components/survey-form/SurveyFormFooter';
import { SurveyFormHeader } from './components/survey-form/SurveyFormHeader';

const getQuestionCardComps = (questionsData, dispatch, surveyId, isPreview) =>
  questionsData.map((question, index) => ({
    id: question.id,
    comp: (
      <EngagementQuestionCard
        key={question.id}
        surveyId={surveyId}
        serialNumber={index + 1}
        question={question}
        dispatch={dispatch}
        isPreview={isPreview}
      />
    ),
  }));

const SurveyQuestionnaireDeerPage = ({ isPreview = false }) => {
  const { surveyId } = useParams() as { surveyId: string };

  const isL2NavVisible = useSelectIsL2Visible();
  const { moduleName } = useOrganisationSettings();
  const applicableL2MyDataModules = getApplicableL2MyDataModules(moduleName) ?? [];

  const [, setForceUpdateNum] = useState(0);
  const [state, dispatch] = useReducerWithThunk(pulseSurveyReducer, pulseSurveyInitialState);

  // following 3 apis calls are only used for Preview mode
  const { data: selectedSurveyTemplate } = useGetSurveySelectedTemplate(surveyId, isPreview); // to get selected survey template id
  const { data: surveyDetails } = useGetSurveyForm(surveyId, isPreview); // to get survey name
  const { data: surveySettings } = useGetSurveySettings(surveyId, isPreview); // to get survey due date

  // survey form filling mode data fetching
  useEffect(() => {
    // @ts-expect-error ignore
    if (surveyId && !isPreview) dispatch(addSurveyDetailsAction(surveyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  // preview form mode data fetching
  useEffect(() => {
    // add data to redux only when all the data is available
    if (selectedSurveyTemplate && 'entity' in selectedSurveyTemplate && surveyDetails && surveySettings) {
      // @ts-expect-error ignore
      dispatch(
        addPreviewSurveyDetailsAction(selectedSurveyTemplate.entity.id, {
          name: surveyDetails.name,
          isAnonymous: surveySettings.visibilityType === EMPLOYEE_PRIVACY_VALUES.ANONYMOUS,
          endDateFormatted: ColorCodedDate.fromJSON({
            timeStamp: surveySettings.endDate ? parseISO(surveySettings.endDate).getTime() : Date.now(),
            color: 'BLUE',
            reason: null,
            isCrossed: false,
          }),
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSurveyTemplate, surveyDetails, surveySettings]);

  const {
    // @ts-expect-error ignore
    endDateFormatted,
    // @ts-expect-error ignore
    name,
    // @ts-expect-error ignore
    questionsData,
    // @ts-expect-error ignore
    currentQuestionId,
    // @ts-expect-error ignore
    isAnonymous,
    // @ts-expect-error ignore
    requiredQuestionsCount,
    // @ts-expect-error ignore
    answeredRequiredQuesCount,
  } = state;

  // *********** key press events START
  const handleKeyNavigation = useCallback((event) => {
    if (['TEXTAREA', 'INPUT'].includes(event.target.nodeName)) {
      return;
    }
    const key = event.key || event.code || 0;
    if (['ArrowUp', 'ArrowLeft'].includes(key)) {
      event.preventDefault();
      // @ts-expect-error ignore
      dispatch(moveToPreviousQuestionAction());
    } else if (['ArrowDown', 'ArrowRight'].includes(key)) {
      event.preventDefault();
      // @ts-expect-error ignore
      dispatch(moveToNextQuestionAction());
    }
  }, []);

  useEffect(() => {
    const debouncedForceUpdate = debounce(500, () => {
      setForceUpdateNum((num) => num + 1);
    });
    document.body.addEventListener('keydown', handleKeyNavigation, true);
    window.addEventListener('resize', debouncedForceUpdate);
    return () => {
      document.body.removeEventListener('keydown', handleKeyNavigation);
      window.removeEventListener('resize', debouncedForceUpdate);
    };
  }, [handleKeyNavigation]);
  // *********** key press events END

  if (!questionsData) {
    return (
      <Flex flexDir="column" h="100%" gap="12px">
        <Skeleton h="80px" w="100%" borderRadius=".5rem" />
        <Skeleton flex="1" w="100%" borderRadius=".5rem" />
      </Flex>
    );
  }

  const questionCards = questionsData ? getQuestionCardComps(questionsData, dispatch, surveyId, isPreview) : [];

  return (
    <Flex flexDir="column" m="-8px -16px" h="100%" w="calc(100% + 32px)">
      {/* Preview Banner */}
      {isPreview && (
        <Flex alignItems="center" minH="44px" bg="functional.FFEFD4" pl="20px" gap="8px" mb="16px">
          <TextBodyMedium>This is a preview for</TextBodyMedium>
          <TextBodyMedium bold>{name}</TextBodyMedium>
        </Flex>
      )}
      {/* Breadcrumbs */}
      {!isPreview && !isNetchexLogin && (
        <Box px="16px" ml={isL2NavVisible ? '0' : '24px'}>
          <DeerBreadcrumbs
            breadcrumbs={{
              items: [
                {
                  label: 'Me',
                  to: applicableL2MyDataModules[0]?.value,
                },
                {
                  label: 'Engagement',
                  to: L3_PATHS['my-data'].engagement.mySurveys,
                },
              ],
              page: {
                label: name,
                to: '',
              },
            }}
          />
        </Box>
      )}
      <Flex flexDir="column" px="16px" gap="12px" flex="1">
        {/* Header */}
        <SurveyFormHeader
          isPreview={isPreview}
          name={name}
          endDateFormatted={endDateFormatted}
          questionsData={questionsData}
        />
        {/* Main */}
        <Card style={{ height: isPreview ? 'calc(100vh - 170px)' : 'calc(100vh - 126px)', padding: 0 }}>
          <CardContent style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <ScrollableQuestionnaire comps={questionCards} selectedCompId={currentQuestionId} />
            {/* Footer */}
            <SurveyFormFooter
              isPreview={isPreview}
              surveyId={surveyId}
              questionsData={questionsData}
              currentQuestionId={currentQuestionId}
              isAnonymous={isAnonymous}
              remainingRequiredQuestionCount={requiredQuestionsCount - answeredRequiredQuesCount}
              // @ts-expect-error ignore
              dispatch={dispatch}
            />
          </CardContent>
        </Card>
      </Flex>
    </Flex>
  );
};

export { SurveyQuestionnaireDeerPage };
